import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { QueryState } from '../components/QueryState'
import { useAdminMe } from '../components/RequireAdmin'
import { ApiError } from '../lib/api'
import { adminApi, can, type WeightConfigList, type WeightConfigSummary } from '../lib/adminApi'

/**
 * 배점 조정 — 과업지시서 §4-라("운영자는 … 평가항목의 가중치를 조정할 수 있어야 한다")의 화면.
 *
 * **화면이 지켜야 하는 두 가지.**
 * 1. 항목 6종은 고정이다(§4-다-3 표 = 계약 구조). 그래서 항목을 추가·삭제하는 UI가 없다 —
 *    숫자 칸 6개만 있다. 이 화면에 "항목 추가" 버튼이 없는 것은 누락이 아니라 설계다.
 * 2. **되돌리기는 별도 기능이 아니다.** 옛 배점을 다시 켜는 것이 롤백이고, 그래서 이력의
 *    모든 행에 "적용" 버튼이 있다. 지난 매칭 결과는 그때의 배점 사본으로 남으므로
 *    되돌려도 과거 기록이 바뀌지 않는다.
 *
 * 값은 **발주기관이 정한다.** 이 화면은 그 값을 넣는 통로일 뿐이다.
 */

function errorMessage(error: unknown): string | null {
  if (error === null || error === undefined) return null
  if (error instanceof ApiError) {
    if (error.status === 403) return '이 작업을 할 권한이 없습니다.'
    if (error.code === 'version_exists') return '같은 이름의 배점이 이미 있습니다 — 다른 이름을 쓰세요.'
    if (error.code === 'not_found') return '없는 배점입니다. 목록을 새로고침해 주세요.'
    if (error.code === 'weight_sum_invalid')
      return '여섯 항목의 배점이 모두 0이어서 적용할 수 없습니다 — 합계가 0보다 커야 합니다.'
    return error.detail[0] ?? error.code
  }
  return '처리 중 오류가 발생했습니다.'
}

export function AdminWeightPage() {
  const me = useAdminMe()
  const mayManage = can(me.data, 'admin', 'manage')
  const list = useQuery({ queryKey: ['admin-weight-configs'], queryFn: () => adminApi.weightConfigs() })

  return (
    <div>
      <div className="admin-bar">
        <div>
          <h1 className="page-title">배점 조정</h1>
          <p className="page-desc">
            매칭 점수를 계산하는 <b>항목별 배점</b>을 관리합니다. 평가 항목 6종은 과업 기준이라
            바뀌지 않고, <b>배점 숫자·등급 경계·노출 임계값</b>을 조정합니다. 지금 켜져 있는 배점 하나만
            매칭에 쓰이며, 지난 결과는 그때의 배점으로 그대로 남습니다.
          </p>
        </div>
        <Link className="btn ghost" to="/admin/match">
          매칭으로
        </Link>
      </div>

      <QueryState isPending={list.isPending} error={list.error} isEmpty={false}>
        {list.data !== undefined && <WeightSections data={list.data} mayManage={mayManage} />}
      </QueryState>
    </div>
  )
}

function WeightSections(props: { data: WeightConfigList; mayManage: boolean }) {
  const { data, mayManage } = props
  const active = data.items.find((c) => c.isActive) ?? null

  return (
    <>
      {mayManage ? (
        <DraftSection data={data} active={active} />
      ) : (
        <p className="muted small">배점 변경은 관리 권한(admin:manage) 계정만 할 수 있습니다.</p>
      )}
      <HistorySection data={data} mayManage={mayManage} />
    </>
  )
}

/**
 * `2026-08-26` → `v20260826`. 이름 규칙(소문자·숫자로 시작, 30자 이내)을 자동으로 만족시킨다.
 *
 * 날짜는 **한국 날짜**로 만든다 — `toISOString()`은 UTC라 한국 시간 오전 9시 이전에 전날이 찍히고,
 * 그러면 「오늘 만든 배점」의 이름이 어제 날짜가 된다. 브라우저 로컬이 아니라 `Asia/Seoul`로
 * 고정하는 이유는 이 이름이 서버에 저장돼 **다른 사람도 같은 이름으로 보기 때문**이다.
 */
function suggestVersion(existing: WeightConfigSummary[]): string {
  const base = `v${new Date().toLocaleDateString('sv-SE', { timeZone: 'Asia/Seoul' }).replaceAll('-', '')}`
  const taken = new Set(existing.map((c) => c.version))
  if (!taken.has(base)) return base
  for (let n = 2; n < 100; n += 1) {
    if (!taken.has(`${base}-${n}`)) return `${base}-${n}`
  }
  return base
}

/**
 * 새 배점 만들기. **현재 값에서 시작한다** — 실제 조정은 "지금 배점에서 몇 점 옮기기"라
 * 빈 폼에서 여섯 칸을 다시 채우게 하면 오히려 틀린 값이 들어간다.
 */
function DraftSection(props: { data: WeightConfigList; active: WeightConfigSummary | null }) {
  const { data, active } = props
  const queryClient = useQueryClient()
  const [weights, setWeights] = useState<Record<string, number>>(() =>
    Object.fromEntries(data.scoringItems.map((i) => [i.code, weightOf(active, i.code)])),
  )
  const [version, setVersion] = useState(() => suggestVersion(data.items))
  const [label, setLabel] = useState('')
  const [memo, setMemo] = useState('')
  const [midMin, setMidMin] = useState(active?.gradeMidMin ?? 40)
  const [highMin, setHighMin] = useState(active?.gradeHighMin ?? 70)
  // 임계값만 문자열로 든다 — **빈 칸("정하지 않았다")과 0("0점도 보인다")이 다른 결정**이라
  // 숫자 상태로는 그 둘을 구분할 수 없다. 서버도 null과 0을 가른다(034).
  const [minVisible, setMinVisible] = useState<string>(
    active?.minVisibleScore === null || active?.minVisibleScore === undefined
      ? ''
      : String(active.minVisibleScore),
  )
  const [activateNow, setActivateNow] = useState(false)
  const [done, setDone] = useState<string | null>(null)

  const sum = data.scoringItems.reduce((a, i) => a + (weights[i.code] ?? 0), 0)
  const activeSum = data.scoringItems.reduce((a, i) => a + weightOf(active, i.code), 0)
  const gradeOk = midMin < highMin
  const minVisibleNum = minVisible.trim() === '' ? null : Number(minVisible)
  const minVisibleOk =
    minVisibleNum === null ||
    (Number.isInteger(minVisibleNum) && minVisibleNum >= 0 && minVisibleNum <= 100)
  // **합계는 고정이 아니다**(v5 · 축 E) — 분모가 「설정된 배점의 합」이라 합이 얼마든
  // 점수는 0~100으로 나온다. 그래서 합계 100을 저장 조건으로 걸지 않는다. 그 조건을
  // 남겨 두면 실데이터에서 전건 미측정인 항목의 배점을 0으로 내릴 수 없다(§4-라가 준
  // 조정 권한이 좁아진다). 막는 것은 0뿐이다 — 분모가 0이면 나눌 수 없다.
  const canSave = sum > 0 && gradeOk && minVisibleOk && label.trim().length > 0 && version.length > 0

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        version: version.trim(),
        label: label.trim(),
        gradeMidMin: midMin,
        gradeHighMin: highMin,
        items: data.scoringItems.map((i) => ({ scoringItem: i.code, weight: weights[i.code] ?? 0 })),
        ...(memo.trim().length > 0 ? { memo: memo.trim() } : {}),
        // 비운 칸은 키를 아예 빼지 않고 **null로 보낸다** — 그래야 "임계값을 없앴다"가
        // 서버에 전달된다(안 보내면 같은 결과지만, 의도가 응답·감사에 남지 않는다).
        minVisibleScore: minVisibleNum,
      }
      const created = await adminApi.createWeightConfig(body)
      // 만들기와 켜기는 서버에서도 두 단계다. 화면이 한 번에 해 주더라도, 만들기가 성공한
      // 뒤 켜기가 실패하면 "만들어졌지만 안 켜진" 상태가 정확한 결과다 — 삭제로 되돌리지 않는다.
      if (activateNow) await adminApi.activateWeightConfig(created.version)
      return created
    },
    onSuccess: (created) => {
      setDone(
        activateNow
          ? `배점 "${created.label}"을 적용했습니다.`
          : `배점 "${created.label}"을 만들었습니다. 아래 목록에서 적용하세요.`,
      )
      setLabel('')
      setMemo('')
      void queryClient.invalidateQueries({ queryKey: ['admin-weight-configs'] })
      void queryClient.invalidateQueries({ queryKey: ['admin-weight-config'] })
    },
  })

  const setWeight = (code: string, raw: string): void => {
    const n = Number(raw)
    if (!Number.isInteger(n) || n < 0 || n > 100) return
    setWeights({ ...weights, [code]: n })
    setDone(null)
  }

  return (
    <section className="card-block">
      <h2>새 배점 만들기</h2>
      <p className="muted small">
        항목 6종은 과업 기준이라 더하거나 뺄 수 없습니다. 여섯 칸의 <b>합계는 100이 아니어도</b>{' '}
        됩니다 — 점수는 언제나 <b>합계를 100점으로 환산</b>해 매깁니다. 쓰지 않기로 한 항목은 0으로
        두면 그만큼 점수 척도에서 빠집니다(합계가 0이면 저장할 수 없습니다).
      </p>

      <table className="admin-table">
        <thead>
          <tr>
            <th>평가 항목</th>
            <th>현재 배점</th>
            <th>새 배점</th>
          </tr>
        </thead>
        <tbody>
          {data.scoringItems.map((item) => (
            <tr key={item.code}>
              <td>{item.label}</td>
              <td className="muted">{weightOf(active, item.code)}</td>
              <td>
                <input
                  type="number"
                  min={0}
                  max={100}
                  aria-label={`${item.label} 배점`}
                  value={weights[item.code] ?? 0}
                  onChange={(e) => setWeight(item.code, e.target.value)}
                />
              </td>
            </tr>
          ))}
          <tr>
            <td>
              <b>합계</b>
            </td>
            {/* 「현재 배점」 칸은 지금 켜져 있는 설정의 실제 합이다 — 표 기준값(100)이 아니다. */}
            <td className="muted">{activeSum}</td>
            <td className={sum > 0 ? 'ok' : 'error'}>
              <b>{sum}</b>
              {sum > 0 ? (
                <span className="small"> · 이 합계를 100점으로 환산합니다</span>
              ) : (
                <span className="small"> · 0보다 커야 합니다</span>
              )}
            </td>
          </tr>
        </tbody>
      </table>

      <div className="form-row">
        <label htmlFor="weight-grade-mid">등급 경계 — 중간</label>
        <input
          id="weight-grade-mid"
          type="number"
          min={0}
          max={100}
          value={midMin}
          onChange={(e) => setMidMin(Number(e.target.value))}
        />
        <p className="muted small">이 점수 이상이면 "중간"으로 표시합니다.</p>
      </div>
      <div className="form-row">
        <label htmlFor="weight-grade-high">등급 경계 — 높음</label>
        <input
          id="weight-grade-high"
          type="number"
          min={0}
          max={100}
          value={highMin}
          onChange={(e) => setHighMin(Number(e.target.value))}
        />
        {!gradeOk && <p className="error small">높음 경계는 중간 경계보다 커야 합니다.</p>}
      </div>

      <div className="form-row">
        <label htmlFor="weight-min-visible">노출 임계값 (선택)</label>
        <input
          id="weight-min-visible"
          type="number"
          min={0}
          max={100}
          placeholder="비우면 제한 없음"
          value={minVisible}
          onChange={(e) => {
            setMinVisible(e.target.value)
            setDone(null)
          }}
        />
        <p className="muted small">
          이 점수 <b>미만인 후보는 시민 화면에 보이지 않습니다</b>. 비워 두면 점수와 상관없이 모두
          보입니다. 점수를 매기지 못한 후보("확인 필요")는 이 값과 무관하게 남습니다.
        </p>
        {!minVisibleOk && <p className="error small">0에서 100 사이의 정수를 넣거나 비워 주세요.</p>}
      </div>

      <div className="form-row">
        <label htmlFor="weight-label">배점 이름</label>
        <input
          id="weight-label"
          type="text"
          maxLength={80}
          placeholder="예: 실증 1차 반영안"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
        />
      </div>
      <div className="form-row">
        <label htmlFor="weight-memo">바꾼 이유 (선택)</label>
        <input
          id="weight-memo"
          type="text"
          maxLength={500}
          placeholder="예: 실증 결과 실행 가능성 비중을 높임"
          value={memo}
          onChange={(e) => setMemo(e.target.value)}
        />
        <p className="muted small">나중에 왜 바꿨는지 알아보기 위한 메모입니다.</p>
      </div>
      <div className="form-row">
        <label htmlFor="weight-version">저장 이름(영문)</label>
        <input
          id="weight-version"
          type="text"
          maxLength={30}
          value={version}
          onChange={(e) => setVersion(e.target.value.toLowerCase())}
        />
        <p className="muted small">
          영문 소문자·숫자로 시작하는 30자 이내. 한 번 쓴 이름은 다시 쓸 수 없습니다.
        </p>
      </div>

      <div className="form-row">
        <label htmlFor="weight-activate">
          <input
            id="weight-activate"
            type="checkbox"
            checked={activateNow}
            onChange={(e) => setActivateNow(e.target.checked)}
          />{' '}
          만들면서 바로 적용
        </label>
        <p className="muted small">
          체크하지 않으면 만들어만 두고, 아래 목록에서 원할 때 적용합니다.
        </p>
      </div>

      <div className="form-actions">
        <button type="button" className="btn" disabled={!canSave || save.isPending} onClick={() => save.mutate()}>
          {activateNow ? '만들고 적용' : '만들기'}
        </button>
      </div>

      {save.error !== null && (
        <p className="error" role="alert">
          {errorMessage(save.error)}
        </p>
      )}
      {done !== null && save.error === null && (
        <p className="ok" role="status">
          {done}
        </p>
      )}
    </section>
  )
}

function weightOf(config: WeightConfigSummary | null, code: string): number {
  return config?.items.find((i) => i.scoringItem === code)?.weight ?? 0
}

/** 이력. **되돌리기가 여기 있다** — 옛 행의 "적용"을 누르는 것이 롤백이다. */
function HistorySection(props: { data: WeightConfigList; mayManage: boolean }) {
  const { data, mayManage } = props
  const queryClient = useQueryClient()
  const [note, setNote] = useState<string | null>(null)

  const activate = useMutation({
    mutationFn: (version: string) => adminApi.activateWeightConfig(version),
    onSuccess: (res) => {
      setNote(
        res.previousVersion === null
          ? `"${res.label}"을 적용했습니다.`
          : `"${res.label}"을 적용했습니다. 직전 배점은 ${res.previousVersion}이며 목록에 그대로 남습니다.`,
      )
      void queryClient.invalidateQueries({ queryKey: ['admin-weight-configs'] })
      void queryClient.invalidateQueries({ queryKey: ['admin-weight-config'] })
    },
  })

  return (
    <section className="card-block">
      <h2>배점 이력</h2>
      <p className="muted small">
        적용 중인 배점은 하나입니다. <b>되돌리려면 예전 배점의 "적용"을 누릅니다</b> — 지난
        매칭 결과는 그때의 배점으로 남아 있어 바뀌지 않습니다. 알고리즘 {data.algorithmVersion}.
      </p>

      <table className="admin-table">
        <thead>
          <tr>
            <th>배점</th>
            {data.scoringItems.map((i) => (
              <th key={i.code}>{i.label}</th>
            ))}
            {/* 노출 임계값은 **열을 늘리지 않고** 등급 경계 칸에 함께 적는다 — 항목 6열이
                이미 있어서 모바일에서 표가 가로로 넘치면 오른쪽 "적용" 버튼이 가려진다
                (E2E가 실제로 클릭 실패로 잡았다). 둘 다 "점수를 어떻게 보여주나"라 한 칸이 맞다. */}
            <th>등급 경계 · 노출</th>
            <th>만든 날</th>
            <th>적용</th>
          </tr>
        </thead>
        <tbody>
          {data.items.map((c) => (
            <tr key={c.version}>
              <td>
                <b>{c.label}</b>
                <div className="muted small">{c.version}</div>
                {c.memo !== null && <div className="muted small">{c.memo}</div>}
              </td>
              {data.scoringItems.map((i) => (
                <td key={i.code}>{weightOf(c, i.code)}</td>
              ))}
              <td className="muted small">
                중간 {c.gradeMidMin} · 높음 {c.gradeHighMin}
                <div>
                  노출 {c.minVisibleScore === null ? '제한 없음' : `${c.minVisibleScore}점 이상`}
                </div>
              </td>
              <td className="muted small">{c.createdAt.slice(0, 10)}</td>
              <td>
                {c.isActive ? (
                  <span className="ok small">적용 중</span>
                ) : mayManage ? (
                  <button
                    type="button"
                    className="btn ghost small-btn"
                    disabled={activate.isPending}
                    onClick={() => activate.mutate(c.version)}
                  >
                    적용
                  </button>
                ) : (
                  <span className="muted small">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {activate.error !== null && (
        <p className="error" role="alert">
          {errorMessage(activate.error)}
        </p>
      )}
      {note !== null && activate.error === null && (
        <p className="ok" role="status">
          {note}
        </p>
      )}
    </section>
  )
}
